import React, { useState, useEffect, useRef, useMemo, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Divider,
  Grid,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress
} from '@mui/material';
import { db } from '../../services/firebase';
import { doc, getDoc} from 'firebase/firestore';
import { formatText, formatTextForDatabase } from '../common/TextFormatterProvider';
import FormulaBuilder, { formulaSyntaxError } from '../data/manage/FormulaBuilder';
import HelpButton from '../common/HelpButton';
import NodeOutputsPreview from './NodeOutputsPreview';
import { authenticatedFetch, fetchGcsObject } from '../../services/api_utils';
import { buildDataSourceManifest, schemaLeafPaths } from '../../utils/manifestSchema';

// A preview posts the API each connected Data Source's saved sample (100 rows
// at most), so it is bounded however large the tables are. The endpoint stops
// at this many seconds; the browser waits a little longer for the reply.
const PREVIEW_TIMEOUT_SECONDS = 120;

const parseConstantValue = (value, type) => {
  if (type === 'array') {
    if (Array.isArray(value)) {
      return value;
    }
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : value.split(',').map(v => {
        const trimmed = v.trim();
        const num = parseFloat(trimmed);
        return isNaN(num) ? trimmed : num;
      });
    } catch {
      return value.split(',').map(v => {
        const trimmed = v.trim();
        const num = parseFloat(trimmed);
        return isNaN(num) ? trimmed : num;
      });
    }
  }
  return value;
};

// The Earned/Incurred API's `earning_schedule` constant spreads each contract's
// premium across the months of its term, so its factors must total 1. The
// backend only checks that the list is non-empty and numeric: a schedule
// totalling 0.9 validates, publishes and runs, and permanently leaves 10% of
// every premium unearned in a table that looks healthy (H96). Returns the
// reason the schedule cannot be saved, or null.
export const earningScheduleProblem = (value) => {
  if (value === undefined || String(value).trim() === '') {
    return 'Enter the monthly earning factors.';
  }
  const schedule = parseConstantValue(Array.isArray(value) ? value : String(value), 'array');
  const factors = schedule.map(Number);
  if (!factors.every(Number.isFinite)) {
    return 'Every earning factor must be a number.';
  }
  const total = factors.reduce((sum, factor) => sum + factor, 0);
  if (Math.abs(total - 1) > 1e-6) {
    return `The earning factors total ${Number(total.toFixed(6))}; they must total 1.`;
  }
  return null;
};

// The manifest a preview commits to the block: the API document's declaration
// with what this preview produced filled in. The declared static columns
// stand; every other column the rows carry is input-dependent and listed as
// present at build, the way the runner lists it at run time. Each declared
// file must have been returned, and nothing undeclared, exactly as the runner
// insists at run time. The files' storage paths are added when the block is
// saved.
export const previewManifest = (declaredManifest, rows, artifacts) => {
  const returnedFiles = new Map(artifacts.map((artifact) => [artifact.filename, artifact]));
  const manifest = declaredManifest.map((entry) => {
    if (entry.kind === 'data') {
      const staticNames = new Set((entry.schema?.static || []).map((column) => column.name));
      const present = [];
      rows.forEach((row) => {
        Object.keys(row || {}).forEach((name) => {
          if (!staticNames.has(name) && !present.includes(name)) present.push(name);
        });
      });
      const dynamic = present.length > 0
        ? { present_at_build: present, note: entry.schema?.dynamic?.note || entry.description || '' }
        : null;
      return { ...entry, schema: { ...entry.schema, dynamic } };
    }
    const artifact = returnedFiles.get(entry.key);
    if (!artifact) {
      throw new Error(`The analysis did not return its declared file "${entry.key}".`);
    }
    returnedFiles.delete(entry.key);
    return { ...entry, size_bytes: new TextEncoder().encode(artifact.content).length };
  });
  if (returnedFiles.size > 0) {
    throw new Error(`The analysis returned a file it does not declare: ${[...returnedFiles.keys()].join(', ')}.`);
  }
  return manifest;
};

const previewObjectUri = (relativePath) => (
  `gs://${process.env.REACT_APP_PLATFORM_FIREBASE_STORAGE_BUCKET}/${relativePath}`
);

const PredefinedBlockConfig = forwardRef(function PredefinedBlockConfig({
  companyName,
  functionId,
  blockId,
  apiDetails,
  onSave,
  onCancel
}, ref) {
  const [name, setName] = useState('');
  const [apiId, setApiId] = useState('');
  const [dataSources, setDataSources] = useState({});
  const [constants, setConstants] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [availableDataSourceNodes, setAvailableDataSourceNodes] = useState([]);
  const [nodeTableColumns, setNodeTableColumns] = useState({});
  const [error, setError] = useState('');
  const [mappingTypes, setMappingTypes] = useState({});
  const [formulaElements, setFormulaElements] = useState({});
  // The preview the block was last saved with, shown until a new one runs.
  const [savedManifest, setSavedManifest] = useState([]);
  const [savedOutputGcsPath, setSavedOutputGcsPath] = useState(null);

  // A preview of the configuration on screen: the analysis's rows, the files
  // it returned, and the manifest they fill in. Any edit invalidates it, so a
  // save never commits a preview of an earlier configuration.
  const [previewData, setPreviewData] = useState(null);
  const [previewFiles, setPreviewFiles] = useState([]);
  const [previewManifestState, setPreviewManifestState] = useState([]);
  const [previewFresh, setPreviewFresh] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const previewGenerationRef = useRef(0);
  const previewAbortRef = useRef(null);

  const invalidatePreview = () => {
    previewGenerationRef.current += 1;
    if (previewAbortRef.current) {
      previewAbortRef.current.abort();
      previewAbortRef.current = null;
    }
    setPreviewFresh(false);
    setPreviewData(null);
    setPreviewFiles([]);
    setPreviewManifestState([]);
    setPreviewError(null);
    setLoadingPreview(false);
  };
  useEffect(() => () => {
    if (previewAbortRef.current) previewAbortRef.current.abort();
  }, []);

  // A returned file is downloadable before the save puts it in storage.
  const fileDownloadUrls = useMemo(() => Object.fromEntries(previewFiles.map((file) => [
    file.filename, URL.createObjectURL(new Blob([file.content], { type: file.mime })),
  ])), [previewFiles]);
  useEffect(() => () => {
    Object.values(fileDownloadUrls).forEach((url) => URL.revokeObjectURL(url));
  }, [fileDownloadUrls]);

  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    setError('');
    const loadData = async () => {
      if (!companyName || !functionId || !blockId || !apiDetails) return;

      try {
        const functionRef = doc(db, `users/${companyName}/data_functions`, functionId);
        const blockRef = doc(functionRef, 'blocks', blockId);
        const functionSnap = await getDoc(functionRef);
        const blockSnap = await getDoc(blockRef);
        if (cancelled) return;

        if (!functionSnap.exists() || !blockSnap.exists()) {
          setError('Function or block document not found.');
          return;
        }

        const functionData = functionSnap.data();
        const blockData = blockSnap.data();
        if (apiDetails.id !== blockData.config?.api_id) {
          setError('The block’s API changed. Close and reopen its configuration.');
          return;
        }

        setName(formatText(blockData.name || ''));
        setApiId(blockData.config?.api_id || '');
        setSavedManifest(blockData.artifacts_manifest || []);
        setSavedOutputGcsPath(blockData.output_data_gcs_path || null);

        const edges = functionData.edges || [];
        const nodes = functionData.nodes || [];

        const predefinedNode = nodes.find(n => n.data.blockId === blockId);
        if (!predefinedNode) {
            setError("Could not find this block in the function graph.");
            return;
        }

        const incomingEdges = edges.filter(e => e.target === predefinedNode.id);
        const dataSourceNodeIds = incomingEdges.map(e => e.source);

        const availableNodes = [];
        const columnsMap = {};

        for (const dsNodeId of dataSourceNodeIds) {
          const sourceNode = nodes.find(node => node.id === dsNodeId);
          if (!sourceNode?.data?.blockId) throw new Error('An input node no longer exists.');
          const sourceBlockId = sourceNode.data.blockId;
          const dsBlockRef = doc(functionRef, 'blocks', sourceBlockId);
          const dsBlockSnap = await getDoc(dsBlockRef);
          if (!dsBlockSnap.exists()) throw new Error('An input block no longer exists.');
          const dsBlockData = dsBlockSnap.data();
          const tableId = dsBlockData.config?.table_id;
          availableNodes.push({
            nodeId: dsNodeId, blockId: sourceBlockId,
            name: formatText(dsBlockData.name || 'Unnamed'), tableId,
            previewPath: dsBlockData.preview_data_gcs_path || null,
          });
          if (!tableId) continue;
          const tableSnap = await getDoc(doc(db, `users/${companyName}/tables`, tableId));
          if (!tableSnap.exists()) throw new Error('An input table no longer exists.');
          const tableData = tableSnap.data();
          const tableName = formatText(tableData.name || tableId);
          const manifestSchema = buildDataSourceManifest(tableData)[0].schema;
          const staticPaths = new Set(schemaLeafPaths(manifestSchema));
          columnsMap[sourceBlockId] = schemaLeafPaths(manifestSchema, true).map(path => ({
            display: `${tableName} // ${staticPaths.has(path) ? formatText(path) : `Dynamic: ${path}`}`,
            value: path,
          }));
        }
        if (cancelled) return;
        setAvailableDataSourceNodes(availableNodes);
        setNodeTableColumns(columnsMap);

        // A saved source that is no longer connected to this block (its node
        // deleted or its edge removed) has no columns to map against: it loads
        // as unselected, mappings included, so it cannot be saved back.
        const connectedBlockIds = new Set(availableNodes.map(n => n.blockId));
        const savedSources = Object.fromEntries(
          Object.entries(blockData.config?.data_sources || {})
            .filter(([, sourceConfig]) => connectedBlockIds.has(sourceConfig?.source_node_id))
        );
        setDataSources(savedSources);

        // Initialize constants with defaults if not already set
        const initialConstants = {};
        if (apiDetails.inputs?.constants) {
          Object.entries(apiDetails.inputs.constants).forEach(([key, constDef]) => {
            if (blockData.config?.constants && blockData.config.constants[key] !== undefined) {
              initialConstants[key] = blockData.config.constants[key];
            } else if (constDef.default !== undefined) {
              initialConstants[key] = constDef.default;
            }
          });
        }
        setConstants(initialConstants);

        const initialMappingTypes = {};
        const initialFormulaElements = {};

        Object.entries(savedSources).forEach(([sourceKey, sourceConfig]) => {
          if (sourceConfig.mappings) {
            Object.entries(sourceConfig.mappings).forEach(([inputName, mappingValue]) => {
              const key = `${sourceKey}.${inputName}`;
              if (mappingValue && mappingValue.includes('`')) {
                initialMappingTypes[key] = 'formula';
                initialFormulaElements[key] = parseFormula(mappingValue);
              } else {
                initialMappingTypes[key] = 'simple';
              }
            });
          }
        });

        setMappingTypes(initialMappingTypes);
        setFormulaElements(initialFormulaElements);
        setLoaded(true);
        setLoadedGeneration((n) => n + 1);
      } catch (err) {
        if (cancelled) return;
        console.error('Error loading predefined block data:', err);
        setError('Failed to load configuration data.');
      }
    };

    loadData();
    return () => { cancelled = true; };
  }, [companyName, functionId, blockId, apiDetails, loadAttempt]);

  const parseFormula = (formulaString) => {
    const parts = formulaString.split('`');
    const elements = [];
    for (let i = 0; i < parts.length; i += 2) {
      if (parts[i].trim()) {
        elements.push({ type: 'variable', value: parts[i].trim() });
      }
      if (i + 1 < parts.length) {
        elements.push({ type: 'operator', value: parts[i + 1].trim() });
      }
    }
    return elements;
  };

  const handleDataSourceChange = (apiSourceKey, nodeBlockId) => {
    const node = availableDataSourceNodes.find(n => n.blockId === nodeBlockId);
    if (!node) return;
    if (dataSources[apiSourceKey]?.source_node_id === nodeBlockId) return;

    invalidatePreview();
    // The mappings name columns of the previous source; none of them carries
    // over to the new one.
    setDataSources(prev => ({
      ...prev,
      [apiSourceKey]: {
        source_node_id: nodeBlockId,
        table_id: node.tableId,
        mappings: {}
      }
    }));
    const ofOtherSources = (entries) => Object.fromEntries(
      Object.entries(entries).filter(([key]) => !key.startsWith(`${apiSourceKey}.`))
    );
    setMappingTypes(ofOtherSources);
    setFormulaElements(ofOtherSources);
  };

  const handleMappingTypeChange = (sourceKey, inputName, newType) => {
    if (!newType) return;
    invalidatePreview();
    const key = `${sourceKey}.${inputName}`;
    setMappingTypes(prev => ({ ...prev, [key]: newType }));

    if (newType === 'simple') {
      setFormulaElements(prev => {
        const { [key]: _, ...rest } = prev;
        return rest;
      });
    } else {
      setDataSources(prev => ({
        ...prev,
        [sourceKey]: {
          ...prev[sourceKey],
          mappings: {
            ...prev[sourceKey]?.mappings,
            [inputName]: ''
          }
        }
      }));
      if (!formulaElements[key]) {
        setFormulaElements(prev => ({ ...prev, [key]: [] }));
      }
    }
  };

  const handleSimpleMappingChange = (sourceKey, inputName, columnValue) => {
    invalidatePreview();
    setDataSources(prev => ({
      ...prev,
      [sourceKey]: {
        ...prev[sourceKey],
        mappings: {
          ...prev[sourceKey]?.mappings,
          [inputName]: columnValue
        }
      }
    }));
  };

  const handleFormulaElementsChange = (sourceKey, inputName, newElements) => {
    invalidatePreview();
    const key = `${sourceKey}.${inputName}`;
    setFormulaElements(prev => ({ ...prev, [key]: newElements }));
  };

  const handleConstantChange = (constantKey, value) => {
    invalidatePreview();
    setConstants(prev => ({ ...prev, [constantKey]: value }));
  };

  // The block's name and configuration as they would be saved: formulas
  // serialised, constants parsed to their declared types.
  const buildConfig = () => {
    const processedDataSources = JSON.parse(JSON.stringify(dataSources));

    Object.entries(processedDataSources).forEach(([sourceKey, sourceConfig]) => {
      if (sourceConfig.mappings) {
        Object.entries(sourceConfig.mappings).forEach(([inputName, mappingValue]) => {
          const key = `${sourceKey}.${inputName}`;
          if (mappingTypes[key] === 'formula') {
            const elements = formulaElements[key] || [];
            sourceConfig.mappings[inputName] = elements
              .map((el) => (el.type === 'operator' ? ` \`${el.value}\` ` : el.value))
              .join('')
              .trim();
          }
        });
      }
    });

    const processedConstants = {};
    if (apiDetails?.inputs?.constants) {
      Object.entries(apiDetails.inputs.constants).forEach(([key, constDef]) => {
        const value = constants[key];
        if (value !== undefined) {
          processedConstants[key] = parseConstantValue(value, constDef?.type);
        } else if (constDef.default !== undefined) {
          processedConstants[key] = parseConstantValue(constDef.default, constDef?.type);
        }
      });
    }

    return {
      name: formatTextForDatabase(name.trim()),
      config: {
        api_id: apiId,
        data_sources: processedDataSources,
        constants: processedConstants
      }
    };
  };

  // Runs the analysis over the connected Data Sources' saved samples, calling
  // the API's endpoint as the signed-in person. Returns the preview, or null
  // when it could not be produced (the reason is shown in the preview pane).
  const handlePreview = async () => {
    if (!loaded) { setError('Wait for the configuration to load, or retry the failed read.'); return null; }
    if (saveBlocked) { setError('Fix the highlighted fields before previewing.'); return null; }

    setError('');
    // Only a completed, successful preview of the current settings is fresh;
    // the previous one stops counting the moment a new attempt starts.
    invalidatePreview();
    const generation = previewGenerationRef.current;
    const controller = new AbortController();
    previewAbortRef.current = controller;
    const timeout = setTimeout(() => controller.abort(), (PREVIEW_TIMEOUT_SECONDS + 15) * 1000);
    setLoadingPreview(true);

    try {
      const { config } = buildConfig();
      const data = {};
      for (const [sourceKey, source] of Object.entries(config.data_sources)) {
        const sourceNode = availableDataSourceNodes.find(n => n.blockId === source.source_node_id);
        if (!sourceNode?.previewPath) {
          throw new Error(`Preview and save the data source "${sourceNode?.name || source.source_node_id}" first.`);
        }
        data[sourceKey] = await fetchGcsObject(previewObjectUri(sourceNode.previewPath));
      }
      const mappings = Object.fromEntries(Object.entries(config.data_sources)
        .map(([sourceKey, source]) => [sourceKey, source.mappings || {}]));

      const response = await authenticatedFetch(apiDetails.endpoint, {
        method: 'POST',
        body: JSON.stringify({
          data,
          mappings,
          constants: config.constants,
          function_id: functionId,
          node_id: blockId,
          timeout_seconds: PREVIEW_TIMEOUT_SECONDS,
        }),
        signal: controller.signal,
      });
      // The endpoint answers a refused or failed call with a JSON {"error": …}
      // body; anything else is shown as the status it came with.
      if (!response.ok) {
        const bodyText = await response.text();
        let message = bodyText;
        try {
          const parsed = JSON.parse(bodyText);
          if (parsed && typeof parsed.error === 'string') message = parsed.error;
        } catch (parseErr) {
          // Plain-text body: shown as is.
        }
        throw new Error(message || `Request failed with status ${response.status}`);
      }
      const body = await response.json();
      if (generation !== previewGenerationRef.current) return null;
      const manifest = previewManifest(apiDetails.manifest, body.data, body.artifacts);
      setPreviewData(body.data);
      setPreviewFiles(body.artifacts);
      setPreviewManifestState(manifest);
      setPreviewFresh(true);
      return { rows: body.data, files: body.artifacts, manifest };
    } catch (err) {
      // A superseded request (aborted by an edit or a newer preview) says
      // nothing: the editor already reflects the newer configuration. An abort
      // on unmount is not an error either.
      if (generation !== previewGenerationRef.current || err?.name === 'AbortError') return null;
      console.error('Preview error:', err);
      setPreviewError(err.message || 'Unknown error');
      return null;
    } finally {
      clearTimeout(timeout);
      if (generation === previewGenerationRef.current) {
        previewAbortRef.current = null;
        setLoadingPreview(false);
      }
    }
  };

  const handleSave = async () => {
    if (!loaded) { setError('Wait for the configuration to load, or retry the failed read.'); return false; }
    if (!apiId) { setError('Please select an API.'); return false; }
    if (!name.trim()) { setError('Please provide a name.'); return false; }
    // Publish/Close save through the ref, which bypasses the disabled Save
    // button — re-apply the same gate here (M188).
    if (saveBlocked) { setError('Fix the highlighted fields before saving.'); return false; }

    // The saved block carries a preview of its current settings. One that has
    // not been run since the last change is run here rather than demanded of
    // the user; a preview that fails leaves its message in the preview pane and
    // nothing is saved.
    let preview = { rows: previewData, files: previewFiles, manifest: previewManifestState };
    if (!previewFresh) {
      preview = await handlePreview();
      if (!preview) return false;
    }
    return onSave({ ...buildConfig(), preview });
  };

  // The builder's Publish and Close ask this editor two things through its ref:
  // whether what is on screen differs from what was loaded, and to save it.
  // The baseline is the editable state as it stood when loading finished.
  const [loadedGeneration, setLoadedGeneration] = useState(0);
  const baselineRef = useRef('');
  const editSignature = JSON.stringify({ name, apiId, dataSources, constants, mappingTypes, formulaElements });
  useEffect(() => {
    baselineRef.current = editSignature;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedGeneration]);
  useImperativeHandle(ref, () => ({
    isDirty: () => loaded && editSignature !== baselineRef.current,
    save: () => handleSave(),
  }));

  const getMappingSupportsFormula = (apiSourceKey, mappingInput) => {
    if (!apiDetails?.inputs?.data_sources?.[apiSourceKey]?.mappings) return false;
    const mapping = apiDetails.inputs.data_sources[apiSourceKey].mappings.find(m => m.input === mappingInput);
    return mapping?.supports_formula || false;
  };

  const getAvailableVariables = (sourceNodeId) => {
    return nodeTableColumns[sourceNodeId]?.map(col => col.value) || [];
  };

  // Every mapping the API declares must name a column of the selected data
  // source (the API schema has no optional mappings). Returns the reason a
  // mapping cannot be saved, or null. A source with no data source selected
  // is not checked, as before.
  const mappingProblem = (sourceKey, inputName) => {
    const source = dataSources[sourceKey];
    if (!source?.source_node_id) return null;
    const columns = new Set(getAvailableVariables(source.source_node_id));
    const key = `${sourceKey}.${inputName}`;
    if ((mappingTypes[key] || 'simple') === 'formula') {
      const syntaxIssue = formulaSyntaxError(formulaElements[key] || []);
      if (syntaxIssue) return syntaxIssue;
      const variables = (formulaElements[key] || []).filter(el => el.type === 'variable');
      if (variables.length === 0) return 'Build a formula from the selected data source\'s columns.';
      return variables.every(el => columns.has(el.value))
        ? null
        : 'This formula uses a column the selected data source does not have.';
    }
    const value = source.mappings?.[inputName];
    if (!value) return 'Choose a column from the selected data source.';
    return columns.has(value) ? null : 'This column is not in the selected data source.';
  };

  // A declared data source left unselected produces an analysis with a
  // silently empty input. The manifests mark these `required`; a source
  // without the flag is treated as required too — the API schemas have no
  // optional sources (M188).
  const missingRequiredSource = (sourceKey) => {
    const sourceConfig = apiDetails?.inputs?.data_sources?.[sourceKey];
    if (!sourceConfig || sourceConfig.required === false) return false;
    return !dataSources[sourceKey]?.source_node_id;
  };

  // Constants are free text, so the only semantic check is the one the API's
  // meaning demands; every other constant is passed through as typed.
  const constantProblem = (constKey) => (
    constKey === 'earning_schedule' ? earningScheduleProblem(constants[constKey]) : null
  );

  const saveBlocked = !loaded || Object.entries(apiDetails?.inputs?.data_sources || {}).some(([sourceKey, sourceConfig]) =>
    missingRequiredSource(sourceKey)
    || (sourceConfig.mappings || []).some(mapping => mappingProblem(sourceKey, mapping.input))
  ) || Object.keys(apiDetails?.inputs?.constants || {}).some(constantProblem);

  const getConstantDisplayValue = (key) => {
    const value = constants[key];
    if (value === undefined) return '';
    if (Array.isArray(value)) {
      return JSON.stringify(value);
    }
    return String(value);
  };

  // The pane shows the fresh preview when there is one, otherwise the preview
  // the block was saved with.
  const showingFreshPreview = previewData !== null;
  const showPreviewPane = showingFreshPreview || previewError || savedManifest.length > 0;

  return (
    <Box sx={{
      p: 2, width: showPreviewPane ? '1500px' : '800px', height: '85vh', position:'absolute', top:100, left:'3.3%',
      zIndex:999, background:'#1A1E26', border:'1px solid rgba(255, 255, 255, 0.08)',
      borderRadius:1, color: '#F0F2F5', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
      borderTop: '3px solid #FF6F61', display: 'flex', flexDirection: 'column'
    }}>
      <Grid container spacing={2} sx={{ flexGrow: 1, overflow: 'hidden' }}>
        <Grid item xs={showPreviewPane ? 6 : 12} sx={{ maxHeight: '78vh', overflowY: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="h6" sx={{ color: '#FF6F61' }}>Configure Predefined Block</Typography>
            <HelpButton
              docPath="dashboard-page/data-functions/building-your-first-function/adding-predefined-node"
              tooltip="Predefined Block Documentation"
            />
          </Box>
          {error && <Typography variant="body2" color="error" sx={{ mb: 2 }}>{error}</Typography>}
          {error && !loaded && <Button onClick={() => setLoadAttempt(attempt => attempt + 1)}>Retry</Button>}
          {/* Save reads the name once its preview has run, so the field is
              closed while one is running rather than losing a keystroke. */}
          <TextField fullWidth size="small" label="Name" disabled={!loaded || loadingPreview} value={name} onChange={(e) => setName(e.target.value)} sx={{ mb:2 }} />

          {loaded && (
            <>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>API: {formatText(apiDetails.name)}</Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>{apiDetails.description}</Typography>

              {apiDetails.inputs?.data_sources && Object.entries(apiDetails.inputs.data_sources).map(([sourceKey, sourceConfig]) => (
                <Box key={sourceKey} sx={{ mb: 3, p: 2, bgcolor: 'rgba(255,255,255,0.02)', borderRadius: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: '#FF6F61' }}>
                    {formatText(sourceKey)} - {sourceConfig.description}
                  </Typography>

                  <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <InputLabel>Select Data Source Block</InputLabel>
                    <Select
                      value={dataSources[sourceKey]?.source_node_id || ''}
                      label="Select Data Source Block"
                      onChange={(e) => handleDataSourceChange(sourceKey, e.target.value)}
                    >
                      <MenuItem value=""><em>Select a data source...</em></MenuItem>
                      {availableDataSourceNodes.map(node => (
                        <MenuItem key={node.blockId} value={node.blockId}>{node.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {missingRequiredSource(sourceKey) && (
                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: -1.5, mb: 1.5 }}>
                      This input is required — select a data source block for it.
                    </Typography>
                  )}

                  {dataSources[sourceKey]?.source_node_id && sourceConfig.mappings &&
                    sourceConfig.mappings.map(mapping => {
                      const key = `${sourceKey}.${mapping.input}`;
                      const supportsFormula = getMappingSupportsFormula(sourceKey, mapping.input);
                      const mappingType = mappingTypes[key] || 'simple';
                      const problem = mappingProblem(sourceKey, mapping.input);

                      return (
                        <Box key={mapping.input} sx={{ mb: 2 }}>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            {mapping.input} - {mapping.description}
                          </Typography>

                          {supportsFormula && (
                            <ToggleButtonGroup
                              value={mappingType}
                              exclusive
                              onChange={(e, newType) => handleMappingTypeChange(sourceKey, mapping.input, newType)}
                              size="small"
                              sx={{ mb: 1 }}
                            >
                              <ToggleButton value="simple">Simple</ToggleButton>
                              <ToggleButton value="formula">Formula</ToggleButton>
                            </ToggleButtonGroup>
                          )}

                          {mappingType === 'simple' ? (
                            <FormControl fullWidth size="small">
                              <Select
                                value={dataSources[sourceKey]?.mappings?.[mapping.input] || ''}
                                onChange={(e) => handleSimpleMappingChange(sourceKey, mapping.input, e.target.value)}
                              >
                                <MenuItem value=""><em>Select a column...</em></MenuItem>
                                {nodeTableColumns[dataSources[sourceKey].source_node_id]?.map(col => (
                                  <MenuItem key={col.value} value={col.value}>{col.display}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : (
                            <FormulaBuilder
                              elements={formulaElements[key] || []}
                              onChange={(newElements) => handleFormulaElementsChange(sourceKey, mapping.input, newElements)}
                              availableVariables={getAvailableVariables(dataSources[sourceKey].source_node_id)}
                            />
                          )}
                          {problem && (
                            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                              {problem}
                            </Typography>
                          )}
                        </Box>
                      );
                    })
                  }
                </Box>
              ))}

              {apiDetails.inputs?.constants && Object.entries(apiDetails.inputs.constants).map(([constKey, constConfig]) => {
                const problem = constantProblem(constKey);
                return (
                  <Box key={constKey} sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>{constConfig.description}</Typography>
                    <TextField
                      fullWidth
                      size="small"
                      label={formatText(constKey)}
                      value={getConstantDisplayValue(constKey)}
                      onChange={(e) => handleConstantChange(constKey, e.target.value)}
                      error={Boolean(problem)}
                      helperText={problem || (constConfig.type === 'array' ? 'Enter comma-separated values or JSON array' : '')}
                    />
                  </Box>
                );
              })}
            </>
          )}

          <Divider sx={{ my: 2 }} />
          <Stack direction="row" spacing={2} alignItems="center">
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saveBlocked || loadingPreview}
              sx={{ backgroundColor: '#FF6F61 !important', '&.Mui-disabled': { backgroundColor: 'rgba(255, 111, 97, 0.35) !important' } }}
            >
              {loadingPreview ? 'Previewing…' : 'Save'}
            </Button>
            <Button variant="contained" color="secondary" onClick={handlePreview} disabled={saveBlocked || loadingPreview}>
              Preview
            </Button>
            <Button variant="text" onClick={onCancel} sx={{ color: '#ADB5BD' }}>Cancel</Button>
            {loadingPreview && <CircularProgress size={18} />}
            {saveBlocked && (
              <Typography variant="caption" sx={{ color: '#ADB5BD' }}>
                Fix the highlighted fields before saving.
              </Typography>
            )}
          </Stack>
        </Grid>
        {showPreviewPane && (
          <Grid item xs={6} sx={{ maxHeight: '78vh', overflowY: 'auto' }}>
            <Typography variant="subtitle1" sx={{ mb: 1, color: '#FF6F61', position: 'sticky', top: 0, backgroundColor: '#1A1E26', zIndex: 2, py: 1 }}>
              {showingFreshPreview || previewError ? 'Preview' : 'Saved preview'}
            </Typography>
            {previewError && !showingFreshPreview ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
                <Typography color="error">The preview could not be produced.</Typography>
                <Typography variant="body2" sx={{ color: 'rgba(240, 242, 245, 0.6)', wordBreak: 'break-word' }}>
                  {previewError}
                </Typography>
                <Button variant="outlined" onClick={handlePreview} disabled={saveBlocked || loadingPreview}>Retry</Button>
              </Box>
            ) : showingFreshPreview ? (
              <NodeOutputsPreview
                manifest={previewManifestState}
                livePreviewData={previewData}
                fileDownloadUrls={fileDownloadUrls}
              />
            ) : (
              <NodeOutputsPreview manifest={savedManifest} outputGcsPath={savedOutputGcsPath} />
            )}
          </Grid>
        )}
      </Grid>
    </Box>
  );
});

export default PredefinedBlockConfig;
